import { ExplicitSelectionAccordion } from '@yes.technology/react-toolkit'
import useFetchSuggestions from 'modules/interaction/hooks/useFetchSuggestions'
import { useEffect } from 'react'
import { ActivityModel } from 'types/shared'
import { FilteractionParams } from 'utils/apiTypes'
import { ActivityInputProps } from '../Activity.types'
import { MULTISELECTION_SEPARATOR } from 'utils/constants'

export type ActivityMultiselectionAccordionProps = {
  id: string
  parentPath: string[]
  filteraction?: string
  filteractionParams?: FilteractionParams[]
  value: ActivityModel['value']
  onChange: ActivityInputProps['handleChange']
} & Pick<
  React.ComponentProps<typeof ExplicitSelectionAccordion>,
  'hideAccordionArrowOnDisable' | 'options' | 'required' | 'status'
>

const ActivityMultiselectionAccordion = ({
  id,
  parentPath,
  filteraction,
  value,
  options = [],
  onChange,
  required,
  hideAccordionArrowOnDisable,
  filteractionParams,
  status
}: ActivityMultiselectionAccordionProps) => {
  const { suggestions, fetchSuggestions } = useFetchSuggestions({
    parentPath,
    filteraction,
    filteractionParams,
    showLoading: true,
    projectFields: { uuid: 1, des: 1, complement: 1 }
  })

  useEffect(() => {
    if (!filteraction) {
      return
    }

    fetchSuggestions('.*')
  }, [fetchSuggestions, filteraction])

  const finalOptions = options.length ? options : suggestions
  const selectedOptions = {
    des:
      (typeof value === 'object' &&
        value?.des?.split(MULTISELECTION_SEPARATOR)) ||
      [],
    id:
      (typeof value === 'object' &&
        value?.id?.split(MULTISELECTION_SEPARATOR)) ||
      []
  }

  const getCheckedOptions = () => {
    let selectedOption: (typeof options)[0] | undefined

    return selectedOptions.id.reduce(
      (accumulator: Record<string, true>, current) => {
        selectedOption = finalOptions.filter(
          (option) => option.uuid === current
        )[0]

        if (selectedOption) {
          accumulator[selectedOption.uuid] = true
        }

        return accumulator
      },
      {}
    )
  }

  const handleChange = ({ des, uuid }: { des: string; uuid: string }) => {
    const index = selectedOptions.id.indexOf(uuid)

    if (index === -1) {
      selectedOptions.des.push(des)
      selectedOptions.id.push(uuid)
    } else {
      selectedOptions.des.splice(index, 1)
      selectedOptions.id.splice(index, 1)
    }

    onChange({
      id: selectedOptions.id.join(MULTISELECTION_SEPARATOR),
      des: selectedOptions.des.join(MULTISELECTION_SEPARATOR)
    })
  }

  const checkedOptions = getCheckedOptions()

  return (
    <ExplicitSelectionAccordion
      variant='multiselection'
      options={finalOptions}
      optionLabelsPosition={'right'}
      id={id}
      required={required}
      onChange={handleChange}
      checkedOption={checkedOptions}
      hideAccordionArrowOnDisable={hideAccordionArrowOnDisable}
      status={status}
      textContentVariant='bordertext-04'
    />
  )
}

export default ActivityMultiselectionAccordion
